import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "context/auth/authentication";
import { loginPage } from "components/pages/Login/login";
import ToastMsg from "components/common/ToastMsg";
import { ImagePaths } from "utils/ImagePath";
import { ExclamationCircleOutlined, EyeInvisibleOutlined, EyeOutlined,  } from "@ant-design/icons";

// Confirm Modal
const ConfirmModal = ({ open, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl p-4 w-[25rem] max-w-md animate-fadeIn">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">
          Already Logged In <ExclamationCircleOutlined />
        </h2>
        <p className="text-[13px] text-gray-600 mb-5 leading-relaxed">
          This account is already active on another device/session. <br />
          Do you want to continue and force this login?
        </p>
        <div className="flex justify-end gap-3 text-sm">
          <button
            onClick={onCancel}
            className="py-2 px-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="py-2 px-3 rounded-lg text-white bg-[#04b7b1] hover:bg-[#04b7b1]/80 focus:ring-2 focus:ring-[#04b7b1] focus:ring-offset-2 transition"
          >
            Yes, Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Typing Text Animation
const TypingText = ({ texts }) => {
  const [text, setText] = useState("");
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    if (index === texts.length) return;

    if (subIndex === texts[index].length + 1 && !reverse) {
      setTimeout(() => setReverse(true), 1500);
      return;
    }

    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % texts.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? 50 : 100);

    setText(texts[index].substring(0, subIndex));
    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, texts]);

  return (
    <h2 className="lg:text-2xl md:text-lg font-medium text-white text-center min-h-[32px]">
      {text}
      <span className="animate-pulse">|</span>
    </h2>
  );
};

const PasswordInput = ({ values, handleChange }) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="relative">
      <label className="block text-[13px] font-medium text-gray-700 mb-1">
        Password
      </label>
      <input
        type={showPassword ? "text" : "password"}
        name="password"
        autoComplete="new-password"
        value={values.password}
        onChange={handleChange}
        className="w-full px-2 py-2 text-[13px] rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-[#04b7b1] transition cursor-default"
        placeholder="Enter your password"
      />

      {/* Toggle Button */}
      <button
        type="button"
        onClick={togglePassword}
        className="absolute right-3 top-8 text-gray-500 hover:text-[#04b7b1] text-sm"
      >
        {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
      </button>
    </div>
  );
}

// Main Login Component
const Login = () => {
  const navigate = useNavigate();
  const { loginauth, isAuthenticated } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [values, setValues] = useState({ username: "", password: "" });
  const [showModal, setShowModal] = useState(false);
  const [forceLoginValues, setForceLoginValues] = useState(null);

  useEffect(() => {
    if (isAuthenticated) navigate("/home");
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const onFinish = (e) => {
    e.preventDefault();
    setLoading(true);

    const onSuccess = (data, fromForcedLogin = false) => {
      if (data?.status === "success") {
        loginauth(data.data, keepSignedIn);
        setLoading(false);
        navigate("/home");
      } else if (data?.status === "fail") {
        setLoading(false);

        if (data.message === "User already logged in" && !fromForcedLogin) {
          setForceLoginValues({ ...values, allow_login: true });
          setShowModal(true);
        } else {
          ToastMsg("error", data.message);
        }
      }
    };

    const onError = (error) => {
      setLoading(false);
      ToastMsg("error", error);
    };

    loginPage(values, onSuccess, onError);
  };

  const handleForceLogin = () => {
    setShowModal(false);
    loginPage(forceLoginValues, (data) => onFinishSuccess(data, true), onFinishError);
  };

  const onFinishSuccess = (data, fromForcedLogin = false) => {
    if (data?.status === "success") {
      loginauth(data.data, keepSignedIn);
      setLoading(false);
      navigate("/home");
    } else if (data?.status === "fail" && !fromForcedLogin) {
      ToastMsg("error", data.message);
    }
  };

  const onFinishError = (error) => {
    setLoading(false);
    ToastMsg("error", error);
  };

  return (
    <div className="h-screen grid grid-cols-12 font-sans">
      {/* Left Side Gradient */}
      <div className="hidden md:flex col-span-5 animated-gradient
     text-white flex-col justify-center items-center p-10 
     animate-gradient bg-[length:400%_400%] relative overflow-hidden">

        {/* Background Image */}
        <img
          src="https://cdn-icons-png.flaticon.com/512/3135/3135675.png" 
          alt="HRMS Illustration"
          className="absolute opacity-10 w-[30%] h-[30%] object-contain"
        />

        {/* Typing Text */}
        <TypingText
          texts={[
            "Welcome to HRBuddie",
            "Your smarter way to manage HR & Payroll",
            "Simplify attendance, payroll, and compliance",
            "Empowering employees with smarter HR tools",
            "Seamless HR solutions for growing businesses",
          ]}
        />
      </div>


      {/* Right Side Login */}
      <div className="col-span-full md:col-span-7 relative flex items-center justify-center p-6">
        {/* Background Image Behind Card */}
        <div
          className="absolute inset-0 bg-[url('https://media.istockphoto.com/id/1847571768/photo/customer-with-bank-manager-in-office-stock-photo.jpg?s=1024x1024&w=is&k=20&c=Y3oAUjmWSEipuQoTva74TkZuZPYSwT9TFsMz-z1R0Kw=')]
          bg-cover bg-center opacity-20"
        ></div>
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm"></div>

        <div className="w-full max-w-sm relative z-10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={ImagePaths.loginLogo.default}
              alt="App Logo"
              className="h-10 w-auto animate-fadeIn"
            />
          </div>

          {/* Card */}
          <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-xl p-6 animate-fadeIn">
            <h2 className="text-center text-lg font-semibold text-gray-800 mb-1">
              Sign In
            </h2>
            <p className="text-center text-[13px] text-gray-500 mb-5">
              Welcome back! Please enter your details.
            </p>

            {/* Form */}
            <form onSubmit={onFinish} className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  autoFocus
                  autoComplete="off"
                  value={values.username}
                  onChange={handleChange}
                  className="w-full px-2 py-2 text-[13px] rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-[#04b7b1] transition cursor-default"
                  placeholder="Enter your username"
                />
              </div>

              {/* Password */}
              <div>
                <PasswordInput values={values} handleChange={handleChange} />
              </div>

              {/* Remember Me + Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={keepSignedIn}
                    onChange={(e) => setKeepSignedIn(e.target.checked)}
                    className="h-4 w-4 accent-[#04b7b1]/80 rounded border-gray-300 focus:ring-[#04b7b1]"
                  />
                  <span className="text-[13px] text-gray-600">Remember Me</span>
                </label>
                <Link
                  to="/forgotpassword"
                  className="text-[13px] text-[#04b7b1] hover:text-[#04b7b1] font-medium"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2 px-4 rounded-lg text-white font-semibold transition ${loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#04b7b1] hover:bg-[#04b7b1]/80 focus:ring-2 focus:ring-[#04b7b1] focus:ring-offset-2"
                  }`}
              >
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        open={showModal}
        onConfirm={handleForceLogin}
        onCancel={() => setShowModal(false)}
      />
    </div>
  );
};

export default Login;
